import { useState, useCallback } from 'react';
import { useSeoMeta } from '@unhead/react';
import { useNavigate } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import { Upload, FileText, Eye, Send, Loader2, X, Image as ImageIcon, AlertCircle } from 'lucide-react';

import { Layout } from '@/components/Layout';
import { MarkdownRenderer } from '@/components/MarkdownRenderer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useToast } from '@/hooks/useToast';
import { parseSubstackHtml, slugify } from '@/lib/substackParser';

export default function Import() {
  useSeoMeta({
    title: 'Import Substack Article | Substr',
    description: 'Import your Substack articles and publish them as Nostr blog posts.',
  });

  const { user } = useCurrentUser();
  const { mutateAsync: publishEvent, isPending: isPublishing } = useNostrPublish();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [summary, setSummary] = useState('');
  const [content, setContent] = useState('');
  const [image, setImage] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [publishedAt, setPublishedAt] = useState<number | undefined>();
  const [dragActive, setDragActive] = useState(false);
  const [fileLoaded, setFileLoaded] = useState(false);

  const handleTitleChange = (value: string) => {
    setTitle(value);
    if (!slugManuallyEdited) {
      setSlug(slugify(value));
    }
  };

  const processFile = useCallback((file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const html = e.target?.result as string;
      try {
        const parsed = parseSubstackHtml(html);
        setTitle(parsed.title);
        setSlug(slugify(parsed.title));
        setSlugManuallyEdited(false);
        setSummary(parsed.subtitle);
        setContent(parsed.content);
        setPublishedAt(parsed.publishedAt);
        if (parsed.images.length > 0) {
          setImage(parsed.images[0]);
        }
        setFileLoaded(true);
        toast({
          title: 'Article imported',
          description: `"${parsed.title}" has been parsed successfully. Review and publish when ready.`,
        });
      } catch {
        toast({
          title: 'Parse error',
          description: 'Could not parse the HTML file. Make sure it\'s a Substack export.',
          variant: 'destructive',
        });
      }
    };
    reader.readAsText(file);
  }, [toast]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer.files[0];
    if (file) processFile(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = () => {
    setDragActive(false);
  };

  const addTag = () => {
    const trimmed = tagInput.trim().toLowerCase();
    if (trimmed && !tags.includes(trimmed)) {
      setTags([...tags, trimmed]);
      setTagInput('');
    }
  };

  const removeTag = (tag: string) => {
    setTags(tags.filter((t) => t !== tag));
  };

  const handlePublish = async () => {
    if (!user) return;
    if (!title.trim() || !content.trim() || !slug.trim()) {
      toast({
        title: 'Missing fields',
        description: 'Title, slug, and content are required.',
        variant: 'destructive',
      });
      return;
    }

    const eventTags: string[][] = [
      ['d', slug],
      ['title', title],
    ];

    if (summary) eventTags.push(['summary', summary]);
    if (image) eventTags.push(['image', image]);
    if (publishedAt) eventTags.push(['published_at', String(publishedAt)]);
    tags.forEach((t) => eventTags.push(['t', t]));

    try {
      const event = await publishEvent({
        kind: 30023,
        content,
        tags: eventTags,
      });

      const naddr = nip19.naddrEncode({
        kind: 30023,
        pubkey: event.pubkey,
        identifier: slug,
      });

      toast({
        title: 'Published!',
        description: 'Your article has been published to Nostr.',
      });

      navigate(`/${naddr}`);
    } catch {
      toast({
        title: 'Publish failed',
        description: 'Could not publish the article. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const resetForm = () => {
    setTitle('');
    setSlug('');
    setSlugManuallyEdited(false);
    setSummary('');
    setContent('');
    setImage('');
    setTags([]);
    setTagInput('');
    setPublishedAt(undefined);
    setFileLoaded(false);
  };

  if (!user) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto text-center py-20">
          <div className="size-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-6">
            <Upload className="size-8 text-primary" />
          </div>
          <h1 className="text-3xl font-bold font-serif mb-3">Import Substack Articles</h1>
          <p className="text-lg text-muted-foreground mb-6">
            Sign in with your Nostr account to import and publish your Substack articles.
          </p>
          <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <AlertCircle className="size-4" />
            Use the Join button in the top right to get started.
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold font-serif mb-2">Import Article</h1>
          <p className="text-lg text-muted-foreground">
            Upload a Substack HTML export and publish it as a Nostr blog post.
          </p>
        </div>

        {!fileLoaded ? (
          <Card className="border-dashed">
            <CardContent className="py-16 px-8">
              <div
                className={`flex flex-col items-center justify-center text-center rounded-xl border-2 border-dashed p-12 transition-colors ${
                  dragActive
                    ? 'border-primary bg-primary/5'
                    : 'border-muted-foreground/20 hover:border-muted-foreground/40'
                }`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
              >
                <div className="size-14 rounded-2xl bg-muted flex items-center justify-center mb-4">
                  <FileText className="size-7 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold mb-1">Drop your Substack HTML file here</h3>
                <p className="text-sm text-muted-foreground mb-5 max-w-sm">
                  Export your articles from Substack (Settings &rarr; Exports), then drop an HTML file here to import it.
                </p>
                <label>
                  <input
                    type="file"
                    accept=".html,.htm"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <Button variant="outline" className="cursor-pointer" asChild>
                    <span>
                      <Upload className="size-4 mr-2" />
                      Choose File
                    </span>
                  </Button>
                </label>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <Badge variant="secondary" className="gap-1.5">
                <FileText className="size-3" />
                Article loaded
              </Badge>
              <Button variant="ghost" size="sm" onClick={resetForm}>
                <X className="size-4 mr-1" />
                Clear
              </Button>
            </div>

            <Tabs defaultValue="edit" className="w-full">
              <TabsList className="w-full">
                <TabsTrigger value="edit" className="flex-1">
                  <FileText className="size-4 mr-1.5" />
                  Edit
                </TabsTrigger>
                <TabsTrigger value="preview" className="flex-1">
                  <Eye className="size-4 mr-1.5" />
                  Preview
                </TabsTrigger>
              </TabsList>

              <TabsContent value="edit" className="space-y-6 mt-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Article Metadata</CardTitle>
                    <CardDescription>Review and edit the article details before publishing.</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Title</label>
                      <Input
                        value={title}
                        onChange={(e) => handleTitleChange(e.target.value)}
                        placeholder="Article title"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium">Slug (d-tag identifier)</label>
                      <Input
                        value={slug}
                        onChange={(e) => {
                          setSlug(e.target.value);
                          setSlugManuallyEdited(true);
                        }}
                        placeholder="article-slug"
                      />
                      <p className="text-xs text-muted-foreground">
                        Used as the unique identifier for this article on Nostr.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium">Summary</label>
                      <Textarea
                        value={summary}
                        onChange={(e) => setSummary(e.target.value)}
                        placeholder="A brief summary of the article"
                        className="min-h-20"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium flex items-center gap-2">
                        <ImageIcon className="size-4" />
                        Cover Image URL
                      </label>
                      <Input
                        value={image}
                        onChange={(e) => setImage(e.target.value)}
                        placeholder="https://..."
                      />
                      {image && (
                        <img
                          src={image}
                          alt="Cover preview"
                          className="w-full max-h-48 object-cover rounded-lg mt-2"
                        />
                      )}
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium">Tags</label>
                      <div className="flex gap-2">
                        <Input
                          value={tagInput}
                          onChange={(e) => setTagInput(e.target.value)}
                          placeholder="Add a tag..."
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              addTag();
                            }
                          }}
                        />
                        <Button variant="outline" onClick={addTag} type="button">
                          Add
                        </Button>
                      </div>
                      {tags.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {tags.map((tag) => (
                            <Badge key={tag} variant="secondary" className="gap-1">
                              {tag}
                              <button
                                type="button"
                                onClick={() => removeTag(tag)}
                                className="ml-0.5 hover:text-destructive"
                              >
                                <X className="size-3" />
                              </button>
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Content (Markdown)</CardTitle>
                    <CardDescription>Edit the article content in Markdown format.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Textarea
                      value={content}
                      onChange={(e) => setContent(e.target.value)}
                      className="min-h-[400px] font-mono text-sm"
                      placeholder="Your article content in Markdown..."
                    />
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="preview" className="mt-6">
                <Card>
                  <CardContent className="py-8 px-6 md:px-12">
                    {image && (
                      <img
                        src={image}
                        alt={title}
                        className="w-full max-h-80 object-cover rounded-xl mb-8"
                      />
                    )}
                    <h1 className="text-3xl md:text-4xl font-bold font-serif mb-3">{title}</h1>
                    {summary && (
                      <p className="text-lg text-muted-foreground mb-6">{summary}</p>
                    )}
                    <Separator className="mb-8" />
                    <MarkdownRenderer content={content} />
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>

            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={resetForm}>
                Cancel
              </Button>
              <Button onClick={handlePublish} disabled={isPublishing || !title || !content || !slug}>
                {isPublishing ? (
                  <>
                    <Loader2 className="size-4 mr-2 animate-spin" />
                    Publishing...
                  </>
                ) : (
                  <>
                    <Send className="size-4 mr-2" />
                    Publish to Nostr
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
